import { Schema } from 'effect';
import { create } from 'zustand';
import { readStored, writeStored } from '~/utils/browserStorage';

import { BetaPageId } from './betaPages';

const STORAGE_KEY = 'trm-admin.beta-choices';

const StoredChoices = Schema.Struct({
  optedIn: Schema.Array(Schema.String),
  dismissedOffers: Schema.Array(Schema.String),
});

type BetaChoices = {
  readonly optedIn: readonly BetaPageId[];
  readonly dismissedOffers: readonly BetaPageId[];
};

const noChoices: BetaChoices = { optedIn: [], dismissedOffers: [] };

const knownPages = (ids: readonly string[]) => ids.filter(Schema.is(BetaPageId));

export const fromStored = (stored: typeof StoredChoices.Type): BetaChoices => ({
  optedIn: knownPages(stored.optedIn),
  dismissedOffers: knownPages(stored.dismissedOffers),
});

const withPage = (pages: readonly BetaPageId[], page: BetaPageId) =>
  pages.includes(page) ? pages : [...pages, page];

const withoutPage = (pages: readonly BetaPageId[], page: BetaPageId) =>
  pages.filter((each) => each !== page);

export const optIn = (choices: BetaChoices, page: BetaPageId): BetaChoices => ({
  optedIn: withPage(choices.optedIn, page),
  dismissedOffers: withoutPage(choices.dismissedOffers, page),
});

export const optOut = (choices: BetaChoices, page: BetaPageId): BetaChoices => ({
  ...choices,
  optedIn: withoutPage(choices.optedIn, page),
});

export const dismissOffer = (choices: BetaChoices, page: BetaPageId): BetaChoices => ({
  ...choices,
  dismissedOffers: withPage(choices.dismissedOffers, page),
});

export const useBetaChoicesStore = create<{
  choices: BetaChoices;
  tryBeta: (page: BetaPageId) => void;
  switchToClassic: (page: BetaPageId) => void;
  dismissOffer: (page: BetaPageId) => void;
}>()((set, get) => {
  const update = (next: BetaChoices) => {
    writeStored(STORAGE_KEY, StoredChoices, next);
    set({ choices: next });
  };

  return {
    choices: fromStored(readStored(STORAGE_KEY, StoredChoices, noChoices)),
    tryBeta: (page) => update(optIn(get().choices, page)),
    switchToClassic: (page) => update(optOut(get().choices, page)),
    dismissOffer: (page) => update(dismissOffer(get().choices, page)),
  };
});
